import React, { useState, useEffect } from "react";
import { displayDate } from "../../utils/dateUtils";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  PersonAdd,
  Edit,
  Delete,
  Search,
  Visibility,
  Download,
  Upload,
  Security,
  ToggleOn,
  ToggleOff,
  Person as PersonIcon,
  School,
  SupervisorAccount,
  AdminPanelSettings,
} from "@mui/icons-material";
import DataImportWizard from "../../components/common/DataImportWizard";
import PermissionGate from "../../components/common/PermissionGate";
import userService from "../../services/userService";
import instituteService from "../../services/instituteService";
import api from "../../services/api";
import { useDepartmentsByInstitute } from "../../hooks/queries/useDepartments";
import { useRoles } from "../../hooks/queries/useRBAC";
import { revealUserPii } from "../../services/piiService";
import { usePermissions } from "../../hooks/usePermissions";

const AllUsersManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {
      return {};
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInstitute, setSelectedInstitute] = useState("all");
  const [selectedRole, setSelectedRole] = useState("all");
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50, total: 0, totalPages: 0 });
  const [counts, setCounts] = useState({ teachers: 0, students: 0, activeUsers: 0, total: 0 });
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // Whether a reveal request is in flight (used to disable the reveal buttons).
  const [revealingUserId, setRevealingUserId] = useState(null);
  const { hasPermission } = usePermissions();
  // superAdmin holds this implicitly server-side; gate the UI on the same slug.
  const canRevealUserPii = hasPermission("user.pii.reveal");
  // A value is masked when the backend returns a "***"-style marker.
  const looksMasked = (v) => typeof v === "string" && v.includes("***");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    role: "",
    instituteId: "",
    departmentId: "",
    status: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const formsEqual = (a, b) => {
    if (!a || !b) return false;
    const keys = [
      "firstName",
      "lastName",
      "email",
      "phoneNumber",
      "role",
      "instituteId",
      "departmentId",
      "status",
    ];
    const norm = (v) => (v == null ? "" : String(v).trim());
    return keys.every((k) => norm(a[k]) === norm(b[k]));
  };
  const [formErrors, setFormErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");

  const emptyForm = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    role: "",
    instituteId: "",
    departmentId: "",
    status: true,
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setOriginalForm(null);
    setFormErrors({});
    setFormData({ ...emptyForm });
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setEditingId(null);
    setOriginalForm(null);
    setFormErrors({});
    setFormData({ ...emptyForm });
    setIsCreating(false);
  };

  // Backend enum expects camelCase (e.g. "superAdmin"), not "superadmin"
  const normalizeRoleForApi = (role) => {
    if (!role) return role;
    const r = String(role).toLowerCase();
    if (r === "superadmin") return "superAdmin";
    return role;
  };

  const { data: rolesRes, isError: rolesError } = useRoles();
  const rolesFromApi = Array.isArray(rolesRes?.data) ? rolesRes.data : (Array.isArray(rolesRes) ? rolesRes : []);
  const roleColorMap = {
    superAdmin: "error",
    admin: "warning",
    institute: "warning",
    teacher: "info",
    student: "success",
    hod: "secondary",
  };
  const roleIconMap = {
    superAdmin: <SupervisorAccount />,
    admin: <AdminPanelSettings />,
    institute: <AdminPanelSettings />,
    teacher: <School />,
    student: <PersonIcon />,
    hod: <School />,
  };
  const defaultRoles = [
    { id: "superAdmin", label: "Super Admin", color: "error", icon: <SupervisorAccount /> },
    { id: "admin", label: "Admin", color: "warning", icon: <AdminPanelSettings /> },
    { id: "teacher", label: "Teacher", color: "info", icon: <School /> },
    { id: "student", label: "Student", color: "success", icon: <PersonIcon /> },
    { id: "hod", label: "HOD", color: "secondary", icon: <School /> },
  ];
  const roles =
    !rolesError && Array.isArray(rolesFromApi) && rolesFromApi.length > 0
      ? rolesFromApi.map((r) => ({
          id: r.slug || r.role,
          label: r.displayName || r.name || (r.slug === "superAdmin" ? "Super Admin" : r.slug === "hod" ? "HOD" : (r.slug || "").charAt(0).toUpperCase() + (r.slug || "").slice(1)),
          color: roleColorMap[r.slug] || "default",
          icon: roleIconMap[r.slug] || <Security />,
          requiresInstitute: !!r.requiresInstitute,
          requiresDepartment: !!r.requiresDepartment,
        }))
      : defaultRoles.map((r) => ({
          ...r,
          requiresInstitute: ["student", "teacher", "hod"].includes(r.id),
          requiresDepartment: ["student", "teacher", "hod"].includes(r.id),
        }));

  useEffect(() => {
    loadInstitutes();
  }, []);

  // Debounced fetch — one effect avoids duplicate API calls when filters and page reset overlap
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, searchQuery ? 300 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitute, selectedRole, searchQuery, pagination.page, pagination.pageSize]);

  // Load departments when institute is selected in form
  useEffect(() => {
    if (formData.instituteId) {
      loadDepartments(formData.instituteId);
    } else {
      setDepartments([]);
    }
  }, [formData.instituteId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedRole !== "all") {
        params.role = selectedRole;
      }
      if (selectedInstitute && selectedInstitute !== "all") {
        params.instituteId = selectedInstitute;
      }
      if (searchQuery?.trim()) {
        params.search = searchQuery.trim();
      }

      // Server pagination (API is 1-based pages)
      params.page = pagination.page + 1;
      params.limit = pagination.pageSize;

      const response = await userService.getUsers(params);
      const body = response?.data?.data || {};
      const apiCounts = body?.counts || {};
      const apiPagination = body?.pagination || {};
      const rawUsers = response.data.data.users || response.data.data || [];
      setUsers(
        rawUsers.map((u) => {
          const teacherEntry = u?.Teachers?.[0] || u?.Teacher || null;
          const studentEntry = u?.Students?.[0] || u?.Student || null;
          const instituteFromAssociation =
            u?.Institutes?.[0] ||
            u?.Institute ||
            teacherEntry?.Institute ||
            studentEntry?.Institute ||
            null;

          const derivedInstituteId =
            u?.instituteId ??
            teacherEntry?.instituteId ??
            studentEntry?.instituteId ??
            instituteFromAssociation?.id ??
            "";

          const derivedInstituteName =
            u?.instituteName ??
            instituteFromAssociation?.name ??
            "";

          const derivedDepartmentId =
            u?.departmentId ??
            teacherEntry?.departmentId ??
            studentEntry?.departmentId ??
            "";

          const derivedDepartmentName =
            u?.departmentName ??
            teacherEntry?.department?.name ??
            studentEntry?.department?.name ??
            "";

          return {
            ...u,
            instituteId: derivedInstituteId,
            instituteName: derivedInstituteName,
            departmentId: derivedDepartmentId,
            departmentName: derivedDepartmentName,
          };
        })
      );

      setCounts({
        teachers: Number(apiCounts.teachers || 0),
        students: Number(apiCounts.students || 0),
        activeUsers: Number(apiCounts.activeUsers || 0),
        total: Number(apiPagination.total || apiCounts.activeUsers || rawUsers.length || 0),
      });
      setPagination((prev) => ({
        ...prev,
        total: Number(apiPagination.total || 0),
        totalPages: Number(apiPagination.totalPages || 0),
      }));
    } catch (error) {
      console.error("Failed to load users:", error);
      setUsers([]);
      setCounts({ teachers: 0, students: 0, activeUsers: 0, total: 0 });
      setPagination((prev) => ({ ...prev, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const loadInstitutes = async () => {
    try {
      const response = await instituteService.getInstitutes({ limit: 99999 });
      setInstitutes(response.data.data.institutes || response.data.data || []);
    } catch (error) {
      console.error("Failed to load institutes:", error);
      setInstitutes([]);
    }
  };

  const loadDepartments = async (instituteId) => {
    try {
      setDepartmentsLoading(true);
      const response = await instituteService.getInstituteDepartments(instituteId);
      // API returns data as an array directly in response.data.data
      const departmentsData = Array.isArray(response.data.data) ? response.data.data : (response.data.data.departments || []);
      setDepartments(departmentsData);
    } catch (error) {
      console.error("Failed to load departments:", error);
      setDepartments([]);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // Reveal a user's real email/phone (dob) and merge into the open dialog(s).
  // Never persisted; only updates local component state (details view + edit
  // form + the row in the list so a re-open shows the revealed value).
  const handleRevealUserPii = async (userId) => {
    if (!userId || revealingUserId) return;
    setRevealingUserId(userId);
    try {
      const res = await revealUserPii(userId);
      const real = res?.data?.data || {};
      const pick = (target) => {
        if (!target) return target;
        const next = { ...target };
        if (real.email !== undefined && real.email !== null) next.email = real.email;
        if (real.phoneNumber !== undefined && real.phoneNumber !== null)
          next.phoneNumber = real.phoneNumber;
        if (real.dob !== undefined && real.dob !== null) next.dob = real.dob;
        return next;
      };
      // Details dialog
      setSelectedUser((prev) => (prev && prev.id === userId ? pick(prev) : prev));
      // Edit form (only when editing this same user)
      if (editingId === userId) {
        setFormData((prev) => {
          const next = pick(prev);
          // Clear the masked-value phone error now that a real value is present.
          setFormErrors((errs) => {
            const { phoneNumber, ...rest } = errs || {};
            return rest;
          });
          return next;
        });
        setOriginalForm((prev) => (prev ? pick(prev) : prev));
      }
      // Keep the list row in sync so re-opening shows the revealed value.
      setUsers((prev) =>
        Array.isArray(prev)
          ? prev.map((u) => (u.id === userId ? pick(u) : u))
          : prev
      );
    } catch (e) {
      setToastMessage(
        e?.response?.data?.message || "Could not reveal personal information"
      );
      setToastSeverity("error");
    } finally {
      setRevealingUserId(null);
    }
  };

  const getRoleIcon = (role) => {
    const roleObj = roles.find((r) => r.id === role);
    return roleObj ? roleObj.icon : <PersonIcon />;
  };

  const getRoleColor = (role) => {
    const roleObj = roles.find((r) => r.id === role);
    return roleObj ? roleObj.color : "default";
  };

  // Backend already filters by institute and search, so no need for client-side filtering
  const filteredUsers = users;

  const columns = [
    {
      field: "fullName",
      headerName: "User",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: `${getRoleColor(params.row.role)}.main`,
              width: 40,
              height: 40,
            }}
          >
            {getRoleIcon(params.row.role)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.fullName || `${params.row.firstName} ${params.row.lastName}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "role",
      headerName: "Role",
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={getRoleIcon(params.value)}
          label={params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
          size="small"
          color={getRoleColor(params.value)}
        />
      ),
    },
    {
      field: "phoneNumber",
      headerName: "Phone",
      width: 130,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          size="small"
          color={params.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "instituteName",
      headerName: "Institute",
      width: 220,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 120,
      renderCell: (params) =>
        params.value ? displayDate(params.value, "N/A") : "N/A",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedUser(params.row);
              setViewDialogOpen(true);
            }}
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              // Institute/department from flat row, Teachers[0] (teachers/hod), or Students[0] (students)
              const teacherEntry = params.row.Teachers?.[0] || params.row.Teacher;
              const studentEntry = params.row.Students?.[0] || params.row.Student;
              const instituteId = params.row.instituteId ?? teacherEntry?.instituteId ?? teacherEntry?.Institute?.id ?? studentEntry?.instituteId ?? studentEntry?.Institute?.id ?? "";
              const departmentId = params.row.departmentId ?? teacherEntry?.departmentId ?? teacherEntry?.department?.id ?? studentEntry?.departmentId ?? studentEntry?.department?.id ?? "";
              // A masked phone (e.g. "91***3892") is NOT a real number — feeding
              // it to the phone widget makes it misparse and fail validation, and
              // it must never be sent back as the "new" value. Render a masked
              // phone as empty in the field; the submit step omits any still-masked
              // email/phone so the backend keeps the real stored value. Email is a
              // plain text field, so it can display the mask, but is likewise
              // omitted from the payload unless the user actually edits it.
              const rawPhone = params.row.phoneNumber || params.row.phone || "";
              const init = {
                firstName: params.row.firstName || params.row.fullName?.split(" ")[0] || "",
                lastName: params.row.lastName || "",
                email: params.row.email || "",
                phoneNumber: looksMasked(rawPhone) ? "" : rawPhone,
                role: normalizeRoleForApi(params.row.role) || "",
                instituteId: instituteId || "",
                departmentId: departmentId || "",
                status: !!params.row.status,
              };
              setEditingId(params.row.id);
              setFormData(init);
              setOriginalForm(init);
              setFormErrors({});
              setCreateDialogOpen(true);
            }}
          >
            <Edit />
          </IconButton>
          <PermissionGate permission="user.update">
            <IconButton
              size="small"
              onClick={async () => {
                try {
                  await userService.updateUser(params.row.id, { status: !params.row.status });
                  setToastMessage("User status updated successfully");
                  setToastSeverity("success");
                  loadUsers();
                } catch (error) {
                  setToastMessage(
                    error?.response?.data?.message ||
                      "Failed to update user status"
                  );
                  setToastSeverity("error");
                }
              }}
              color={params.row.status ? "success" : "default"}
            >
              {params.row.status ? <ToggleOn /> : <ToggleOff />}
            </IconButton>
          </PermissionGate>
          <PermissionGate permission="user.delete">
            {['superadmin','admin','institute','hod'].includes((currentUser?.role || '').toLowerCase()) && (
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  const studentEntry = params.row.Students?.[0] || params.row.Student;
                  const deleteId = (params.row.role === "student" && studentEntry)
                    ? (studentEntry.id ?? params.row.id)
                    : params.row.id;
                  setDeleteTarget({
                    id: deleteId,
                    userId: params.row.id,
                    role: params.row.role,
                    name: params.row.fullName || `${params.row.firstName} ${params.row.lastName}`,
                  });
                  setDeleteDialogOpen(true);
                }}
              >
                <Delete />
              </IconButton>
            )}
          </PermissionGate>
        </Box>
      ),
    },
  ];

  const validateForm = () => {
    const errors = {};
    const fn = (formData.firstName || "").trim();
    if (!fn) {
      errors.firstName = "First name is required";
    } else if (fn.length < 2 || fn.length > 50) {
      errors.firstName = "Min 2, max 50 characters";
    }
    const ln = (formData.lastName || "").trim();
    if (!ln) {
      errors.lastName = "Last name is required";
    } else if (ln.length < 3 || ln.length > 50) {
      errors.lastName = "Min 3, max 50 characters";
    }
    // A still-masked email (edit mode, untouched) is omitted from the payload so
    // the backend keeps the real value — don't validate the mask as if typed.
    if (!looksMasked(formData.email)) {
      if (!formData.email || formData.email.trim().length === 0) {
        errors.email = "Email is required";
      } else {
        // basic email regex
        const re = /^(([^<>()\[\]\\.,;:\s@\"]+(\.[^<>()\[\]\\.,;:\s@\"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@\"]+\.)+[^<>()[\]\\.,;:\s@\"]{2,})$/i;
        if (!re.test(formData.email)) {
          errors.email = "Enter a valid email";
        }
      }
    }
    if (!formData.role) {
      errors.role = "Role is required";
    }
    // Phone validation: at least 10 digits. A masked phone is rendered empty on
    // open, so an empty phone in EDIT mode means "unchanged" (omitted from the
    // payload) and must not be flagged as required. In CREATE mode phone is
    // required.
    const phoneDigits = (formData.phoneNumber || "").replace(/\D/g, "");
    if (!phoneDigits) {
      if (!editingId) errors.phoneNumber = "Phone number is required";
    } else if (phoneDigits.length < 10) {
      errors.phoneNumber = "Invalid phone number";
    }

    // RBAC: use role metadata (requiresInstitute / requiresDepartment) from the
    // roles API — dynamic, not hardcoded. Institute/admin/superAdmin roles do NOT
    // require an institute or department (see backend roleSeeds.js), so only
    // enforce these when the selected role actually requires them.
    const selectedRoleMeta = roles.find((r) => r.id === formData.role || r.id === normalizeRoleForApi(formData.role));
    if (selectedRoleMeta?.requiresInstitute && !formData.instituteId) {
      errors.instituteId = "Institute is required for this role";
    }
    if (selectedRoleMeta?.requiresDepartment && !formData.departmentId) {
      errors.departmentId = "Department is required for this role";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateField = (field, value) => {
    const errors = { ...formErrors };
    if (field === "firstName") {
      const v = String(value || "").trim();
      if (!v) {
        errors.firstName = "First name is required";
      } else if (v.length < 2 || v.length > 50) {
        errors.firstName = "Min 2, max 50 characters";
      } else {
        delete errors.firstName;
      }
    }
    if (field === "lastName") {
      const v = String(value || "").trim();
      if (!v) {
        errors.lastName = "Last name is required";
      } else if (v.length < 3 || v.length > 50) {
        errors.lastName = "Min 3, max 50 characters";
      } else {
        delete errors.lastName;
      }
    }
    if (field === "email") {
      if (looksMasked(value)) {
        // Untouched masked email — omitted from payload; not an error.
        delete errors.email;
      } else if (!value || String(value).trim().length === 0) {
        errors.email = "Email is required";
      } else {
        const re = /^(([^<>()\[\]\\.,;:\s@\"]+(\.[^<>()\[\]\\.,;:\s@\"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@\"]+\.)+[^<>()[\]\\.,;:\s@\"]{2,})$/i;
        if (!re.test(String(value))) {
          errors.email = "Enter a valid email";
        } else {
          delete errors.email;
        }
      }
    }
    if (field === "phoneNumber") {
      const phoneDigits = (value || "").replace(/\D/g, "");
      if (!phoneDigits) {
        // Empty in edit mode = unchanged (masked value rendered empty), allowed.
        if (!editingId) errors.phoneNumber = "Phone number is required";
        else delete errors.phoneNumber;
      } else if (phoneDigits.length < 10) {
        errors.phoneNumber = "Invalid phone number";
      } else {
        delete errors.phoneNumber;
      }
    }
    if (field === "role") {
      if (!value) {
        errors.role = "Role is required";
      } else {
        delete errors.role;
      }
    }
    if (field === "instituteId") {
      const selectedRoleMeta = roles.find((r) => r.id === formData.role || r.id === normalizeRoleForApi(formData.role));
      if (selectedRoleMeta?.requiresInstitute && !value) {
        errors.instituteId = "Institute is required for this role";
      } else {
        delete errors.instituteId;
      }
    }
    if (field === "departmentId") {
      const selectedRoleMeta = roles.find((r) => r.id === formData.role || r.id === normalizeRoleForApi(formData.role));
      if (selectedRoleMeta?.requiresDepartment && !value) {
        errors.departmentId = "Department is required for this role";
      } else {
        delete errors.departmentId;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Block number keys in first/last name (letters and spaces only)
  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleFirstNameChange = (e) => {
    const value = e.target.value.replace(/\d/g, "").slice(0, 50);
    setFormData((prev) => ({ ...prev, firstName: value }));
    validateField("firstName", value);
  };
  const handleLastNameChange = (e) => {
    const value = e.target.value.replace(/\d/g, "").slice(0, 50);
    setFormData((prev) => ({ ...prev, lastName: value }));
    validateField("lastName", value);
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;
    try {
      setIsCreating(true);
      if (editingId) {
        // Edit: send instituteId and departmentId in body for PUT /users/:id (teacher/hod get them from form)
        const updatePayload = {
          ...formData,
          role: normalizeRoleForApi(formData.role), // Include role in update payload
        };
        // Never overwrite real PII with a mask: drop email/phone that the user
        // did not actually change. A still-masked email means "unchanged"; a
        // masked phone was rendered empty on open, so an empty phone here also
        // means "unchanged". The backend keeps the stored real value for any
        // omitted field.
        if (looksMasked(updatePayload.email)) delete updatePayload.email;
        const phoneDigits = String(updatePayload.phoneNumber || "").replace(/\D/g, "");
        if (!phoneDigits) delete updatePayload.phoneNumber;
        await userService.updateUser(editingId, updatePayload);
        setToastMessage("User updated successfully");
        setToastSeverity("success");
      } else {
        // Create: base payload
        const payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          role: normalizeRoleForApi(formData.role),
        };
        // RBAC: send instituteId/departmentId when role requires them
        const sel = roles.find((r) => r.id === formData.role || r.id === normalizeRoleForApi(formData.role));
        if (formData.role !== "superAdmin" && formData.instituteId) payload.instituteId = formData.instituteId;
        if (sel?.requiresDepartment) payload.departmentId = formData.departmentId;
        await userService.createUser(payload);
        setToastMessage("User created successfully");
        setToastSeverity("success");
      }
      closeCreateDialog();
      loadUsers();
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        role: "",
        instituteId: "",
        departmentId: "",
      });
      setEditingId(null);
      setFormErrors({});
    } catch (error) {
      console.error("Failed to create user:", error);
      const msg = error.response?.data?.message || error.message;
      setFormErrors({ submit: msg });
    } finally {
      setIsCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { id, role } = deleteTarget;
    setIsDeleting(true);
    try {
      // Role-scoped delete endpoints
      if (role === "teacher") {
        await api.delete("/institute/delete-teacher", { params: { id } });
      } else if (role === "student") {
        await api.delete("/teacher/delete-student", { params: { id } });
      } else if (role === "admin") {
        // Super admin route for deleting admin users
        await api.delete("/super/admin/admins-delete", { params: { id } });
      } else {
        // Fallback: try generic user delete (may 404 if not available)
        await userService.deleteUser(id);
      }

      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadUsers();
      setToastMessage("User deleted successfully");
      setToastSeverity("success");
    } catch (err) {
      console.error("Failed to delete user:", err);
      setToastMessage(err.response?.data?.message || err.message || "Failed to delete user");
      setToastSeverity("error");
      // keep dialog open so user can retry or cancel
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const params = { limit: 500 };
      if (selectedRole !== "all") params.role = selectedRole;
      if (selectedInstitute && selectedInstitute !== "all") params.instituteId = selectedInstitute;
      if (searchQuery?.trim()) params.search = searchQuery.trim();

      const blob = await userService.exportUsers(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `users_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export users:", error);
      setToastMessage("Failed to export users");
      setToastSeverity("error");
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportComplete = () => {
    loadUsers();
    setImportDialogOpen(false);
  };

  const stats = [
    {
      label: "Total Users",
      value: counts.total || pagination.total || 0,
      color: "primary.main",
    },
    {
      label: "Teachers",
      value: counts.teachers || 0,
      color: "info.main",
    },
    {
      label: "Students",
      value: counts.students || 0,
      color: "success.main",
    },
    {
      label: "Active Users",
      value: counts.activeUsers || 0,
      color: "warning.main",
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            All Users Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all users across all institutes
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGate permission="user.import">
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          </PermissionGate>
          <PermissionGate permission="user.export">
            <Button
              variant="outlined"
              startIcon={exportLoading ? <CircularProgress size={16} /> : <Download />}
              onClick={handleExport}
              disabled={filteredUsers.length === 0 || exportLoading}
            >
              {exportLoading ? "Exporting…" : "Export"}
            </Button>
          </PermissionGate>
          <PermissionGate permission="user.create">
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={openCreateDialog}
            >
              Add User
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                placeholder="Search by name or email..."
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Institute</InputLabel>
                <Select
                  value={selectedInstitute}
                  label="Institute"
                  onChange={(e) => setSelectedInstitute(e.target.value)}
                >
                  <MenuItem value="all">All Institutes</MenuItem>
                  {institutes.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={selectedRole}
                  label="Role"
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 600, width: "100%" }}>
              <DataGrid
                rows={filteredUsers}
                columns={columns}
                pagination
                paginationMode="server"
                rowCount={pagination.total || 0}
                paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
                onPaginationModelChange={(model) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: model.page ?? prev.page,
                    pageSize: model.pageSize ?? prev.pageSize,
                  }));
                }}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                disableSelectionOnClick
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closeCreateDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingId ? "Edit User" : "Add New User"}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {formErrors.submit && (
              <Alert severity="error" onClose={() => setFormErrors((prev) => ({ ...prev, submit: undefined }))}>
                {formErrors.submit}
              </Alert>
            )}
            <TextField
              label="First Name"
              fullWidth
              required
              value={formData.firstName}
              onChange={handleFirstNameChange}
              onKeyDown={blockNumberKeyDown}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName || (formData.firstName.length > 0 ? (formData.firstName.length < 2 ? "Min 2 characters" : `${formData.firstName.length}/50`) : "Min 2, max 50 characters")}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Last Name"
              fullWidth
              required
              value={formData.lastName}
              onChange={handleLastNameChange}
              onKeyDown={blockNumberKeyDown}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName || (formData.lastName.length > 0 ? (formData.lastName.length < 3 ? "Min 3 characters" : `${formData.lastName.length}/50`) : "Min 3, max 50 characters")}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              error={!!formErrors.email}
              helperText={formErrors.email}
              onChange={(e) => {
                const v = e.target.value;
                setFormData({ ...formData, email: v });
                validateField('email', v);
              }}
            />
            <Box>
              <PhoneInput
                country={"in"}
                value={formData.phoneNumber}
                onChange={(phone) => {
                  setFormData({ ...formData, phoneNumber: phone });
                  validateField('phoneNumber', phone);
                }}
                inputStyle={{ width: '100%', height: 56, borderRadius: 8 }}
                containerStyle={{ width: '100%' }}
                buttonStyle={{ borderRadius: '8px 0 0 8px' }}
                specialLabel="Phone *"
              />
              {formErrors.phoneNumber && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.phoneNumber}
                </Typography>
              )}
            </Box>

            {/* Reveal masked email/phone while editing. A masked email or an
                empty phone (masked value rendered blank on open) means the real
                value is hidden; revealing fills the real values so they can be
                seen/edited. Only offered to callers with reveal permission. */}
            {editingId && canRevealUserPii &&
              (looksMasked(formData.email) ||
                !String(formData.phoneNumber || "").replace(/\D/g, "")) && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => handleRevealUserPii(editingId)}
                  disabled={revealingUserId === editingId}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {revealingUserId === editingId
                    ? "Revealing..."
                    : "Reveal email & phone"}
                </Button>
              )}

            <FormControl fullWidth error={!!formErrors.role} required>
              <InputLabel>Role *</InputLabel>
              <Select
                value={formData.role}
                label="Role *"
                onChange={(e) => {
                  const v = e.target.value;
                  const sel = roles.find((r) => r.id === v || r.id === normalizeRoleForApi(v));
                  // Clear institute/department when the new role doesn't need them
                  // so stale scope from a previous role isn't submitted or shown.
                  setFormData((prev) => ({
                    ...prev,
                    role: v,
                    instituteId: sel?.requiresInstitute ? prev.instituteId : "",
                    departmentId: sel?.requiresDepartment ? prev.departmentId : "",
                  }));
                  setFormErrors((errs) => {
                    const next = { ...(errs || {}) };
                    if (!sel?.requiresInstitute) delete next.instituteId;
                    if (!sel?.requiresDepartment) delete next.departmentId;
                    return next;
                  });
                  validateField('role', v);
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.role && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.role}
                </Typography>
              )}
            </FormControl>

            {editingId && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status ? "active" : "inactive"}
                  label="Status"
                  onChange={(e) => {
                    const nextStatus = e.target.value === "active";
                    setFormData((prev) => ({ ...prev, status: nextStatus }));
                    setFormErrors((prev) => {
                      if (!prev || Object.keys(prev).length === 0) return prev;
                      const { submit, ...rest } = prev;
                      return rest;
                    });
                  }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Institute and Department fields — only shown for roles that
                actually require them (hod/teacher/student). institute/admin/
                superAdmin roles are not institute/department-scoped. */}
            {(() => {
              const sel = roles.find((r) => r.id === formData.role || r.id === normalizeRoleForApi(formData.role));
              if (!formData.role) return false;
              if (formData.role === "superAdmin") return false;
              return !!(sel?.requiresInstitute || sel?.requiresDepartment);
            })() && (
              <>
                <FormControl fullWidth error={!!formErrors.instituteId} required>
                  <InputLabel>Institute *</InputLabel>
                  <Select
                    value={formData.instituteId || ""}
                    label="Institute *"
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData({ ...formData, instituteId: v, departmentId: "" });
                      validateField("instituteId", v);
                    }}
                  >
                    {institutes.map((inst) => (
                      <MenuItem key={inst.id} value={inst.id}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.instituteId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {formErrors.instituteId}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth error={!!formErrors.departmentId} required={roles.find((r) => r.id === formData.role || r.id === normalizeRoleForApi(formData.role))?.requiresDepartment}>
                  <InputLabel>Department *</InputLabel>
                  <Select
                    value={formData.departmentId || ""}
                    label="Department *"
                    disabled={!formData.instituteId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData({ ...formData, departmentId: v });
                      validateField("departmentId", v);
                    }}
                  >
                    {departmentsLoading?(
                      <MenuItem disabled>Loading departments...</MenuItem>
                    ):(() => {
                      const list=departments.filter((dept) => dept.instituteId === formData.instituteId);
                      if(!list.length)return <MenuItem disabled>No departments found</MenuItem>;
                      return list.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ));
                    })()}
                  </Select>
                  {formErrors.departmentId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {formErrors.departmentId}
                    </Typography>
                  )}
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={(() => {
              // In EDIT mode, email may still be masked and phone rendered empty
              // (both meaning "unchanged"); don't treat those as missing.
              const emailPresent = editingId
                ? (looksMasked(formData.email) || !!formData.email)
                : !!formData.email;
              const phonePresent = editingId ? true : !!formData.phoneNumber;
              const requiredPresent = formData.firstName && formData.lastName && emailPresent && formData.role && phonePresent;
              if (!requiredPresent) return true;
              const sel = roles.find((r) => r.id === formData.role || r.id === normalizeRoleForApi(formData.role));
              // Only block on institute/department when the role actually requires them.
              if (sel?.requiresInstitute && !formData.instituteId) return true;
              if (sel?.requiresDepartment && !formData.departmentId) return true;
              const fieldErrorKeys = Object.keys(formErrors || {}).filter((k) => k !== "submit");
              if (fieldErrorKeys.length > 0) return true;
              if (isCreating) return true;
              if (editingId) {
                return originalForm ? formsEqual(originalForm, formData) : false;
              }
              return false;
            })()}
          >
            {isCreating ? (editingId ? "Updating..." : "Creating...") : (editingId ? "Update User" : "Create User")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; if (!isDeleting) { setDeleteDialogOpen(false); setDeleteTarget(null); } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteTarget ? `Are you sure you want to delete ${deleteTarget.name}?` : "Are you sure you want to delete this user?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); setSelectedUser(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ textAlign: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: "auto",
                      mb: 1,
                      bgcolor: `${getRoleColor(selectedUser.role)}.main`,
                    }}
                  >
                    {getRoleIcon(selectedUser.role)}
                  </Avatar>
                  <Typography variant="h6">
                    {selectedUser.fullName ||
                      `${selectedUser.firstName} ${selectedUser.lastName}`}
                  </Typography>
                  <Chip
                    label={selectedUser.role}
                    color={getRoleColor(selectedUser.role)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{selectedUser.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    {selectedUser.phoneNumber || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body2">
                    <Chip
                      label={selectedUser.status ? "Active" : "Inactive"}
                      size="small"
                      color={selectedUser.status ? "success" : "default"}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {selectedUser.createdAt
                      ? displayDate(selectedUser.createdAt, "N/A")
                      : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {canRevealUserPii &&
            selectedUser &&
            (looksMasked(selectedUser.email) || looksMasked(selectedUser.phoneNumber)) && (
              <Button
                startIcon={<Visibility />}
                onClick={() => handleRevealUserPii(selectedUser.id)}
                disabled={revealingUserId === selectedUser.id}
              >
                {revealingUserId === selectedUser.id ? "Revealing..." : "Reveal"}
              </Button>
            )}
          <Button onClick={() => { setViewDialogOpen(false); setSelectedUser(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Import Wizard */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType="users"
        instituteId={selectedInstitute !== 'all' ? selectedInstitute : undefined}
        onImportComplete={handleImportComplete}
      />

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={4000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={toastSeverity} onClose={() => setToastMessage(null)} sx={{ width: "100%" }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllUsersManagement;